/**
 * WB4 Auxiliary Pipeline — single execution chain for kernel actions.
 *
 * Chain (client → server → client invalidate):
 *   local action log → ACTION_EXECUTED (+ auxiliary refresh)
 *   → appendWorkbenchTimelineEventOutboxFirst (client POST)
 *   → server appendTimelineEventOutboxFirst (same fan-out as mutation paths)
 *   → WORKBENCH_TIMELINE_INVALIDATED (client emit on success; SSE on server)
 *   → evidence query invalidation · ActionFeedbackBanner undo affordance
 *
 * **Client-only vs server-mutation split:**
 * - Client (this module): no domain mutation; outbox enqueue happens on server only.
 * - Server mutation paths: domain row + outbox in one `runWorkbenchTransaction`.
 * Both converge on `appendTimelineEventOutboxFirst` → `processWorkbenchOutboxRow`.
 *
 * **timelineEventId contract:** client pre-generates outbox-first id synchronously on the
 * execution entry and WorkbenchExecutionResult; POST echoes the same id; async patch only
 * runs when server returns a different id or on failure (clear).
 */

import type { WorkbenchAction, WorkbenchKernelExecutionSurface } from '@shared/business-workbench';
import type { WorkbenchActionExecutionEntry } from '@shared/business-workbench/workbench-action-execution-log';
import {
  buildTimelineEventFromKernelAction,
  buildWorkbenchTimelineEventId,
  resolveWorkbenchTimelineObjectRef,
  type WorkbenchTimelineEvent,
} from '@shared/business-workbench/workbench-timeline-event';
import {
  resolveActionRiskTier,
  allowsToastUndo,
} from '@shared/business-workbench/workbench-action-safety';
import { resolveEvidenceStateFromKernelAction } from '@shared/business-workbench/workbench-evidence-state';
import { buildAuxiliaryPipelineSpawnMetadata } from '@shared/business-workbench/workbench-auxiliary-spawn-registry';
import type { WorkbenchRevisionSnapshotPayload } from '@shared/business-workbench/workbench-revision-snapshot';
import { buildRevisionAuditChange } from '@shared/business-workbench/workbench-revision-snapshot';
import type { ObjectRef } from '@shared/business-workbench/workbench-focus';
import { logger } from '@shared/logger';
import { appendWorkbenchTimelineEventOutboxFirst } from '../services/workbenchTimelineApi';
import { getCurrentUser } from '../utils/jwtUtils';
import { emitWorkbenchActionExecuted } from './workbench-auxiliary-events';
import { emitWorkbenchTimelineInvalidated } from './workbench-timeline-events';
import {
  appendWorkbenchActionExecution,
  patchWorkbenchActionExecution,
} from '@shared/business-workbench/workbench-action-execution-log';

/**
 * TG-06 — task family kernel actions whose evidence metadata flows through
 * `runWorkbenchAuxiliaryPipeline` via `resolveEvidenceStateFromKernelAction`.
 *
 * Markers: `'start_assignment_workflow'` · `'drag_drop:assign_task'`
 */
export const TASK_AUXILIARY_PIPELINE_SPAWN_ACTION_IDS = [
  'start_assignment_workflow',
  'drag_drop:assign_task',
] as const;

/**
 * DG-09 — `reference:attach_evidence` spawns `approval_packet` queue (WORKBENCH_AUXILIARY_DOWNSTREAM_SPAWN_REGISTRY).
 * @see buildAuxiliaryPipelineSpawnMetadata('reference:attach_evidence')
 */
export const DOCUMENT_APPROVAL_PACKET_SPAWN_ACTION_ID = 'reference:attach_evidence' as const;
export const DOCUMENT_APPROVAL_PACKET_DOWNSTREAM_REF = 'approval_packet' as const;

export const DOCUMENT_AUXILIARY_PIPELINE_SPAWN_ACTION_IDS = [
  DOCUMENT_APPROVAL_PACKET_SPAWN_ACTION_ID,
] as const;

export interface WorkbenchAuxiliaryPipelineInput {
  tenantId: string;
  objectRef: ObjectRef;
  action: Pick<WorkbenchAction, 'id' | 'label' | 'handlerRef'>;
  undoable?: boolean;
  revisionSnapshotId?: string;
  revisionSnapshotPayload?: WorkbenchRevisionSnapshotPayload;
  recoverabilityTier?: 'low' | 'medium' | 'high' | 'finance';
  workspaceDomain?: string;
  workspaceSubdomain?: string;
  actorUserId?: string;
  surface?: WorkbenchKernelExecutionSurface;
  /** Low-tier inline edit before/after for timeline change contract. */
  timelineChange?: WorkbenchTimelineEvent['change'];
}

export interface WorkbenchAuxiliaryPipelineRemoteResult {
  timelineEventId: string;
}

export interface WorkbenchAuxiliaryPipelineResult {
  entry: WorkbenchActionExecutionEntry;
  /** Resolves when outbox-first timeline append completes (or null on failure). */
  pipeline: Promise<WorkbenchAuxiliaryPipelineRemoteResult | null>;
}

async function appendTimelineEventOutboxFirstClient(
  timelineInput: Parameters<typeof appendWorkbenchTimelineEventOutboxFirst>[0],
  timelineRef: ReturnType<typeof resolveWorkbenchTimelineObjectRef>,
  auditRequired: boolean,
  context: {
    tenantId: string;
    objectId: string;
    actionId: string;
    actionExecutionEntryId: string;
    expectedTimelineEventId: string;
  }
): Promise<WorkbenchAuxiliaryPipelineRemoteResult | null> {
  try {
    const event = await appendWorkbenchTimelineEventOutboxFirst(timelineInput, { auditRequired });
    if (event) {
      emitWorkbenchTimelineInvalidated(timelineRef);
      if (event.id !== context.expectedTimelineEventId) {
        const patched = patchWorkbenchActionExecution({
          entryId: context.actionExecutionEntryId,
          timelineEventId: event.id,
          status: 'succeeded',
        });
        if (patched) {
          emitWorkbenchActionExecuted(patched);
        }
      } else {
        const patched = patchWorkbenchActionExecution({
          entryId: context.actionExecutionEntryId,
          status: 'succeeded',
        });
        if (patched) {
          emitWorkbenchActionExecuted(patched);
        }
      }
      return { timelineEventId: event.id };
    }
    const cleared = patchWorkbenchActionExecution({
      entryId: context.actionExecutionEntryId,
      clearTimelineEventId: true,
      status: 'failed',
    });
    if (cleared) {
      emitWorkbenchActionExecuted(cleared);
    }
    return null;
  } catch (error: unknown) {
    logger.error(
      'workbench_timeline_append_failed',
      error instanceof Error ? error : new Error(String(error)),
      context
    );
    const cleared = patchWorkbenchActionExecution({
      entryId: context.actionExecutionEntryId,
      clearTimelineEventId: true,
      status: 'failed',
    });
    if (cleared) {
      emitWorkbenchActionExecuted(cleared);
    }
    return null;
  }
}

/** Single auxiliary pipeline entry — do not call timeline/audit/emit helpers independently on golden path. */
export function runWorkbenchAuxiliaryPipeline(
  input: WorkbenchAuxiliaryPipelineInput
): WorkbenchAuxiliaryPipelineResult {
  const riskPolicy = resolveActionRiskTier({
    actionId: input.action.id,
    handlerRef: input.action.handlerRef,
    businessDomain: input.objectRef.businessDomain,
  });
  const undoable = input.undoable ?? allowsToastUndo(riskPolicy);
  const timelineEventId = buildWorkbenchTimelineEventId();
  const evidenceState = resolveEvidenceStateFromKernelAction({
    actionId: input.action.id,
    handlerRef: input.action.handlerRef,
  });
  const downstreamSpawn = buildAuxiliaryPipelineSpawnMetadata(input.action.id);
  const spawnFields = downstreamSpawn
    ? {
        spawnKind:
          downstreamSpawn.spawn === 'flow'
            ? ('flow' as const)
            : downstreamSpawn.spawn === 'thread'
              ? ('thread' as const)
              : downstreamSpawn.spawn === 'link'
                ? ('document' as const)
                : ('task' as const),
        ...(downstreamSpawn.spawn === 'flow'
          ? { flowRef: downstreamSpawn.downstreamRef }
          : downstreamSpawn.spawn === 'link'
            ? { documentRef: downstreamSpawn.downstreamRef }
            : {
                taskRef: downstreamSpawn.taskRef ?? downstreamSpawn.downstreamRef,
              }),
      }
    : {};

  const entry = appendWorkbenchActionExecution({
    tenantId: input.tenantId,
    objectRef: input.objectRef,
    actionId: input.action.id,
    actionLabel: input.action.label,
    handlerRef: input.action.handlerRef,
    undoable,
    status: 'pending',
    timelineEventId,
    ...(evidenceState ? { evidenceState } : {}),
    ...spawnFields,
    ...(input.revisionSnapshotId ? { revisionSnapshotId: input.revisionSnapshotId } : {}),
    ...(input.recoverabilityTier ? { recoverabilityTier: input.recoverabilityTier } : {}),
  });

  emitWorkbenchActionExecuted(entry);

  const timelineRef = resolveWorkbenchTimelineObjectRef({
    objectId: input.objectRef.id,
    ...(input.workspaceDomain ? { domain: input.workspaceDomain } : {}),
    ...(input.workspaceSubdomain ? { subdomain: input.workspaceSubdomain } : {}),
    ...(input.objectRef.businessDomain ? { businessDomain: input.objectRef.businessDomain } : {}),
  });

  const currentUser = getCurrentUser();
  const revisionChange =
    input.timelineChange ??
    (input.revisionSnapshotPayload
      ? buildRevisionAuditChange(input.revisionSnapshotPayload, input.objectRef)
      : undefined);

  const timelineInput = buildTimelineEventFromKernelAction({
    tenantId: input.tenantId,
    timelineRef,
    action: input.action,
    actor: {
      userId: input.actorUserId ?? currentUser?.userId ?? 'anonymous',
      ...(currentUser?.role ? { role: currentUser.role } : {}),
    },
    id: timelineEventId,
    ...(revisionChange ? { change: revisionChange } : {}),
    ...(input.timelineChange ? { eventType: 'field_updated' as const } : {}),
    ...(input.recoverabilityTier ? { recoverabilityTier: input.recoverabilityTier } : {}),
    ...(input.revisionSnapshotId ? { revisionSnapshotId: input.revisionSnapshotId } : {}),
    undoable,
    ...(input.surface ? { surface: input.surface } : {}),
  });

  const pipeline = appendTimelineEventOutboxFirstClient(
    timelineInput,
    timelineRef,
    riskPolicy.auditRequired,
    {
      tenantId: input.tenantId,
      objectId: input.objectRef.id,
      actionId: input.action.id,
      actionExecutionEntryId: entry.id,
      expectedTimelineEventId: timelineEventId,
    }
  );

  return { entry, pipeline };
}
